<template>
  <section class="cockpit-panel trend-panel">
    <div class="cockpit-title trend-title">模糊聚类趋势</div>
    <div ref="chartRef" class="trend-chart"></div>
  </section>
</template>

<script setup>
import * as echarts from "echarts";
import { onBeforeUnmount, onMounted, ref } from "vue";

const chartRef = ref(null);
let chartInstance = null;

function formatDateLabel(date) {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${month}-${day}`;
}

function getRecentDateLabels() {
  const today = new Date();
  return [18, 15, 11, 8, 5, 3, 0].map((offset) => {
    const date = new Date(today);
    date.setDate(today.getDate() - offset);
    return formatDateLabel(date);
  });
}

const dateLabels = getRecentDateLabels();

const option = {
  color: ["#d8ff5f", "#52fff1"],
  grid: { top: 72, right: 48, bottom: 74, left: 58 },
  legend: {
    top: 42,
    right: 64,
    itemWidth: 28,
    itemHeight: 5,
    textStyle: { color: "#ffffff", fontSize: 22, fontWeight: "bold" },
    data: ["小麦", "红薯"],
  },
  tooltip: {
    trigger: "axis",
    backgroundColor: "rgba(5, 38, 42, 0.92)",
    borderColor: "rgba(84, 255, 240, 0.5)",
    textStyle: { color: "#fff", fontSize: 22 },
  },
  xAxis: {
    type: "category",
    boundaryGap: false,
    data: dateLabels,
    axisTick: { show: false },
    axisLine: { lineStyle: { color: "rgba(138, 255, 247, 0.34)" } },
    axisLabel: { color: "#ffffff", fontSize: 22, fontWeight: "bold", margin: 14 },
    splitLine: { show: true, lineStyle: { color: "rgba(138, 255, 247, 0.12)", type: "dashed" } },
  },
  yAxis: {
    type: "value",
    min: 0,
    max: 100,
    interval: 20,
    axisTick: { show: false },
    axisLine: { show: false },
    axisLabel: { color: "#dffffc", fontSize: 22, fontWeight: "bold" },
    splitLine: { lineStyle: { color: "rgba(138, 255, 247, 0.14)", type: "dashed" } },
  },
  series: [
    {
      name: "小麦",
      type: "line",
      smooth: false,
      symbol: "circle",
      symbolSize: 8,
      lineStyle: { width: 4, color: "#d8ff5f" },
      itemStyle: { color: "#d8ff5f" },
      areaStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: "rgba(216, 255, 95, 0.58)" },
          { offset: 1, color: "rgba(216, 255, 95, 0.03)" },
        ]),
      },
      data: [58, 63, 66, 70, 73, 76, 78],
      markPoint: {
        symbol: "circle",
        symbolSize: 1,
        label: {
          show: true,
          color: "#ffffff",
          fontSize: 21,
          fontWeight: "bold",
          formatter: ({ name }) => name,
          offset: [0, -16],
        },
        data: [
          { name: "小麦", coord: [dateLabels[3], 70] },
          { name: "小麦", coord: [dateLabels[6], 78] },
        ],
      },
    },
    {
      name: "红薯",
      type: "line",
      smooth: false,
      symbol: "circle",
      symbolSize: 8,
      lineStyle: { width: 4, color: "#52fff1" },
      itemStyle: { color: "#52fff1" },
      areaStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: "rgba(82, 255, 241, 0.42)" },
          { offset: 1, color: "rgba(82, 255, 241, 0.02)" },
        ]),
      },
      data: [64, 67, 71, 74, 78, 81, 84],
      markPoint: {
        symbol: "circle",
        symbolSize: 1,
        label: {
          show: true,
          color: "#ffffff",
          fontSize: 21,
          fontWeight: "bold",
          formatter: ({ name }) => name,
          offset: [0, -16],
        },
        data: [
          { name: "红薯", coord: [dateLabels[6], 84] },
        ],
      },
    },
  ],
};

function resizeChart() {
  chartInstance && chartInstance.resize();
}

onMounted(() => {
  chartInstance = echarts.init(chartRef.value);
  chartInstance.setOption(option);
  window.addEventListener("resize", resizeChart);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", resizeChart);
  chartInstance && chartInstance.dispose();
});
</script>

<style lang="less" scoped>
@import "./rightPanelShared.less";

.trend-panel {
  align-self: center;
  height: 72%;
  padding: 76px 34px 34px;
}

.trend-title {
  left: 50%;
  top: -31px;
  z-index: 999;
  min-width: 312px;
  justify-content: center;
  padding-left: 38px;
  transform: translateX(-50%);
 clip-path: polygon(
  26px 0,                /* 左上内收点 */
  calc(100% - 26px) 0,   /* 右上内收点 */
  100% 50%,              /* 右侧尖角 */
  calc(100% - 26px) 100%,/* 右下内收点 */
  26px 100%,             /* 左下内收点 */
  0 50%                  /* 左侧尖角 */
); 
border: 1px solid rgba(82, 255, 240, 0.32);
background: linear-gradient(90deg, rgba(7, 105, 103, 1.0), rgba(7, 82, 87, 1.0));
}

.trend-title::after {
  right: -140px;
}

.trend-chart {
  width: 100%;
  height: calc(100% - 48px);
}

.date-band {
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 21px;
  font-weight: 900;
  line-height: 28px;
  background: linear-gradient(90deg, rgba(13, 104, 105, 0.62), rgba(18, 147, 135, 0.62), rgba(13, 104, 105, 0.62));
  text-shadow: 0 0 8px rgba(255, 255, 255, 0.25);
}
</style>
